import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { motion } from "framer-motion";
import { CheckCircle, PaperPlaneTilt, CalendarBlank, ArrowRight, GiftIcon, MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Logo, PoweredByMangoCubs } from "@/components/Logo";
import PhoneInput from "@/components/PhoneInput";
import { NameInput } from "@/lib/validators";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useSiteAnalytics } from "@/lib/useSiteAnalytics";

const AGE_GROUPS = ["Playgroup", "Nursery", "LKG", "UKG"];
const SOURCES = ["Website", "Referral", "Walk-in", "Google Ads", "Meta Ads", "Event", "WhatsApp"];

export default function PublicEnquiry() {
  useSiteAnalytics();
  const { t } = useTranslation();
  const { brand, isCoBranded } = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCodeFromUrl = searchParams.get("ref");
  const [referrer, setReferrer] = useState(null);
  const [form, setForm] = useState({
    parent_name: "", child_name: "", child_dob: null, age_group: "Nursery",
    phone: "", email: "",
    source: refCodeFromUrl ? "Referral" : "Website",
    branch_id: "", notes: "",
    referral_code: refCodeFromUrl || "",
    interested_in: "preschool",
    location: "",
    preferred_callback_time: "",
    confirm_channel: "whatsapp",
    // Wave R3: split parent role at enquiry-time too.
    relationship: "Mother",
    guardian_type: "",
    // Wave R7 — structured family blocks (Father / Mother / Guardian).
    // Each block now carries its own `show_socials` flag, optional
    // `instagram` / `facebook` handles, and an explicit `whatsapp_number`
    // that is only used when `whatsapp_same_as_phone` is false.
    father: { name: "", phone: "", email: "", occupation: "",
              whatsapp_same_as_phone: true, whatsapp_number: "",
              preferred_contact: false, show_socials: false,
              instagram: "", facebook: "" },
    mother: { name: "", phone: "", email: "", occupation: "",
              whatsapp_same_as_phone: true, whatsapp_number: "",
              preferred_contact: true, show_socials: false,
              instagram: "", facebook: "" },
    guardian_enabled: false,
    guardian: { name: "", phone: "", email: "", occupation: "",
                whatsapp_same_as_phone: true, whatsapp_number: "",
                preferred_contact: false, show_socials: false,
                instagram: "", facebook: "",
                guardian_type: "", pickup_permission: false, emergency_contact: false },
  });
  const [phoneValid, setPhoneValid] = useState(false);
  const [branches, setBranches] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEnq, setSubmittedEnq] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  // QR-scope state — derived from URL query params.
  //
  //   ?branch=br_xxx&token=xxx     → branch-scoped QR (single school).
  //                                  Hide the dropdown completely.
  //   ?franchise=fr_xxx&token=xxx  → franchise-HQ QR (any school under
  //                                  the network). Show a SEARCHABLE
  //                                  dropdown filtered to that franchise.
  //   neither                       → public form / generic enquiry.
  //                                  Show the unfiltered branch dropdown.
  const [qrScope, setQrScope] = useState({
    kind: "public", branchId: null, franchiseId: null,
    branchName: "", franchiseName: "",
  });
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);

  useEffect(() => {
    if (refCodeFromUrl) {
      api.get(`/referrals/by-code/${refCodeFromUrl}`)
        .then(({ data }) => setReferrer(data))
        .catch(() => setReferrer(null));
    }
    // QR token pre-fills branch_id when the parent scanned a school's poster.
    const branchHint = searchParams.get("branch");
    const franchiseHint = searchParams.get("franchise");
    const token = searchParams.get("token");
    if (branchHint) {
      // Single-branch QR. Lock the branch + hide the picker.
      setForm((f) => ({ ...f, branch_id: branchHint }));
      setQrScope({
        kind: "branch", branchId: branchHint, franchiseId: null,
        branchName: "", franchiseName: "",
      });
      // Fetch this one branch (so the success banner can show its name).
      api.get("/branches", { params: { public: true } })
        .then(({ data }) => setBranches((data || []).filter((b) => b.branch_id === branchHint)))
        .catch(() => setBranches([]));
      if (token) {
        api.get("/qr-forms/verify", { params: { branch: branchHint, token } })
          .then(({ data }) => {
            if (data?.valid) {
              setQrScope((s) => ({ ...s,
                branchName: data.branch_name || "",
                franchiseId: data.franchise_id || null,
              }));
            }
          })
          .catch(() => {});
      }
    } else if (franchiseHint) {
      // HQ QR — fetch ALL branches under that franchise for a searchable dropdown.
      setQrScope({
        kind: "hq", branchId: null, franchiseId: franchiseHint,
        branchName: "", franchiseName: "",
      });
      api.get("/branches", { params: { public: true, franchise_id: franchiseHint } })
        .then(({ data }) => setBranches(data || []))
        .catch(() => setBranches([]));
      if (token) {
        api.get("/qr-forms/verify", { params: { franchise: franchiseHint, token } })
          .then(({ data }) => {
            if (data?.valid) {
              setQrScope((s) => ({ ...s, franchiseName: data.franchise_name || "" }));
            }
          })
          .catch(() => {});
      }
    } else {
      // No QR — public form lists every active branch.
      api.get("/branches", { params: { public: true } })
        .then(({ data }) => setBranches(data || []))
        .catch(() => setBranches([]));
    }
  }, [refCodeFromUrl, searchParams]);

  // Convenience lookups used when rendering the searchable branch picker.
  const selectedBranch = useMemo(
    () => branches.find((b) => b.branch_id === form.branch_id) || null,
    [branches, form.branch_id]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone || !phoneValid) {
      setErr("Please enter a valid phone number with country code.");
      return;
    }
    setLoading(true); setErr("");
    try {
      // Auto-fill structured "father" / "mother" block from the primary
      // fields if the parent typed only at the top of the form.
      const primary = form.relationship?.toLowerCase() || "mother";
      const familyBlocks = {
        father: form.father.name || form.father.phone || form.father.email
          ? form.father : (primary === "father" ? {
              name: form.parent_name, phone: form.phone, email: form.email,
              whatsapp_same_as_phone: true, preferred_contact: true,
            } : null),
        mother: form.mother.name || form.mother.phone || form.mother.email
          ? form.mother : (primary === "mother" ? {
              name: form.parent_name, phone: form.phone, email: form.email,
              whatsapp_same_as_phone: true, preferred_contact: true,
            } : null),
        guardian: form.guardian_enabled
          ? { ...form.guardian,
              guardian_type: form.guardian.guardian_type || form.guardian_type }
          : (primary === "guardian" ? {
              name: form.parent_name, phone: form.phone, email: form.email,
              guardian_type: form.guardian_type,
              whatsapp_same_as_phone: true, preferred_contact: true,
            } : null),
      };
      const payload = {
        ...form,
        child_dob: form.child_dob ? form.child_dob.toISOString().slice(0, 10) : null,
        father: familyBlocks.father,
        mother: familyBlocks.mother,
        guardian: familyBlocks.guardian,
        // Drop the UI-only helper field.
        guardian_enabled: undefined,
      };
      const { data } = await api.post("/enquiries", payload);
      setSubmittedEnq(data);
      setSubmitted(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center bg-white rounded-3xl border border-border p-10 shadow-floating">
          <div className="w-16 h-16 mx-auto rounded-full bg-sage-light flex items-center justify-center text-sage-deep">
            <CheckCircle size={36} weight="duotone" />
          </div>
          <h2 className="font-heading text-3xl font-bold mt-4">{t("common.thanks")}</h2>
          <p className="text-ink-soft mt-2">{t("enquiry.thanksSub")}</p>
          {submittedEnq?.form_number && (
            <div className="mt-5 inline-block bg-mustard-soft px-4 py-3 rounded-2xl text-sm" data-testid="enquiry-form-number">
              Your form number:
              <div className="font-mono font-bold text-base mt-1">{submittedEnq.form_number}</div>
              <p className="text-xs text-ink-soft mt-1">Save this — you'll need it to edit later.</p>
            </div>
          )}
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={() => navigate(`/admission?enquiry_id=${submittedEnq?.enquiry_id || ""}`)}
              className="px-6 py-3 rounded-full bg-terracotta text-white font-semibold inline-flex items-center justify-center gap-2"
              data-testid="enquiry-start-admission"
            >
              {t("admission.title")} <ArrowRight weight="bold" />
            </button>
            <Link to="/modify" className="px-6 py-3 rounded-full border border-border font-semibold" data-testid="enquiry-edit-link">
              Edit my form later
            </Link>
            <Link to="/" className="px-6 py-3 rounded-full border border-border font-semibold" data-testid="enquiry-back-home">
              {t("enquiry.backHome")}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="enquiry-brand">
            <Logo size={48} tagline={true} />
          </Link>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" data-testid="enquiry-headline">
          {isCoBranded ? t("enquiry.titleCoBrand", { school: brand.display_name }) : t("enquiry.title")}
        </h1>
        <p className="text-ink-soft mt-3 text-lg">
          {isCoBranded
            ? (brand.tagline ? `${brand.tagline}. ${t("enquiry.subCoBrand")}` : t("enquiry.subCoBrand"))
            : t("enquiry.sub")}
        </p>
        {isCoBranded && (
          <div className="mt-4 inline-flex items-center gap-2 bg-mango-soft border border-mango/30 rounded-full px-3 py-1 text-xs font-semibold text-mango-deep" data-testid="enquiry-co-brand-strip">
            {brand.display_name} <span className="text-mustard-hover">·</span>
            <PoweredByMangoCubs />
          </div>
        )}

        {referrer && (
          <div className="mt-5 p-4 rounded-2xl bg-mustard-soft border border-mustard text-ink flex items-start gap-3" data-testid="referral-banner">
            <span className="w-10 h-10 rounded-xl bg-mustard text-ink flex items-center justify-center">
              <GiftIcon weight="duotone" />
            </span>
            <div>
              <div className="font-heading font-semibold">You've been invited by {referrer.referrer_name}!</div>
              <div className="text-sm text-ink-soft mt-1">
                Both your families get <span className="font-semibold text-ink">₹1,500 in fee credit</span> when your child joins. Referral code: <span className="font-mono font-bold">{referrer.code}</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="public-enquiry-form">
          {/* ─────── Child details first ─────── */}
          <div className="sm:col-span-2 -mb-1 text-xs font-semibold uppercase tracking-wide text-ink-soft" data-testid="child-section-header">
            Child details
          </div>
          <Field label={t("enquiry.childName")} required>
            <NameInput value={form.child_name}
              onChange={(v) => setForm({ ...form, child_name: v })}
              label="child name" testid="enq-child-name"
              placeholder="e.g. Aarav Sharma" />
          </Field>
          <Field label={t("enquiry.dob")}>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <button type="button" className="input flex items-center justify-between text-left" data-testid="enq-dob">
                  <span className={form.child_dob ? "text-ink" : "text-ink-soft"}>
                    {form.child_dob ? form.child_dob.toLocaleDateString() : t("common.select")}
                  </span>
                  <CalendarBlank size={16} className="text-ink-soft" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.child_dob}
                  onSelect={(d) => { setForm({ ...form, child_dob: d }); setDateOpen(false); }}
                  captionLayout="dropdown"
                  fromYear={2017}
                  toYear={new Date().getFullYear()}
                />
              </PopoverContent>
            </Popover>
          </Field>
          <Field label={t("enquiry.ageGroup")}>
            <Select value={form.age_group} onValueChange={(v) => setForm({ ...form, age_group: v })}>
              <SelectTrigger data-testid="enq-age"><SelectValue /></SelectTrigger>
              <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Interested in">
            <Select value={form.interested_in} onValueChange={(v) => setForm({ ...form, interested_in: v })}>
              <SelectTrigger data-testid="enq-interest"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="preschool">Preschool only</SelectItem>
                <SelectItem value="daycare">Daycare only</SelectItem>
                <SelectItem value="preschool_daycare">Preschool + Daycare</SelectItem>
                <SelectItem value="activity_only">Activity / Camp only</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {/* Branch picker — three render modes:
              · branch-scoped QR → hidden (branch is pre-selected from the QR).
              · HQ QR → SEARCHABLE Command combobox restricted to the franchise.
              · No QR → fall-through native select listing every branch. */}
          {qrScope.kind === "branch" && qrScope.branchName ? (
            <div className="sm:col-span-2 -mt-2 mb-1 inline-flex items-center gap-2 rounded-2xl border border-sage-deep/30 bg-sage-light/40 px-3 py-2 text-sm" data-testid="branch-locked-banner">
              <MapPin weight="duotone" className="text-sage-deep" />
              You're applying to <span className="font-semibold">{qrScope.branchName}</span>
            </div>
          ) : qrScope.kind === "hq" ? (
            <Field
              label={`Pick a ${qrScope.franchiseName || "branch"} location`}
              required
              className="sm:col-span-2"
            >
              <Popover open={branchPickerOpen} onOpenChange={setBranchPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="input flex items-center justify-between text-left"
                    data-testid="enq-hq-branch-trigger"
                  >
                    <span className={selectedBranch ? "text-ink" : "text-ink-soft"}>
                      {selectedBranch
                        ? `${selectedBranch.name}${selectedBranch.city ? ` — ${selectedBranch.city}` : ""}`
                        : "Search by branch, city or pincode"}
                    </span>
                    <MagnifyingGlass size={16} className="text-ink-soft" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command
                    // Custom filter — match across name, city, address & pincode so
                    // typing "Indiranagar" or "560038" also returns the right branch.
                    filter={(value, search) => {
                      if (!search) return 1;
                      return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder="Type a branch name, city or pincode…" data-testid="enq-hq-branch-search" />
                    <CommandList>
                      <CommandEmpty>No branches match.</CommandEmpty>
                      <CommandGroup>
                        {branches.map((b) => {
                          const searchValue = [b.name, b.city, b.address, b.pincode]
                            .filter(Boolean).join(" ").toLowerCase();
                          return (
                            <CommandItem
                              key={b.branch_id}
                              value={searchValue}
                              data-testid={`enq-hq-branch-opt-${b.branch_id}`}
                              onSelect={() => {
                                setForm({ ...form, branch_id: b.branch_id });
                                setBranchPickerOpen(false);
                              }}
                            >
                              <div>
                                <div className="font-medium">{b.name}</div>
                                {(b.city || b.address) && (
                                  <div className="text-xs text-ink-soft">
                                    {[b.address, b.city, b.pincode].filter(Boolean).join(" · ")}
                                  </div>
                                )}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </Field>
          ) : (
            <Field label={t("enquiry.branch")} className="sm:col-span-2">
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="enq-branch"><SelectValue placeholder={t("enquiry.anyBranch")} /></SelectTrigger>
                <SelectContent>
                  {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name} — {b.city}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Your locality / pincode">
            <input data-testid="enq-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input" placeholder="e.g. Indiranagar, 560038" />
          </Field>
          <Field label={t("enquiry.source")}>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger data-testid="enq-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Preferred callback time" className="sm:col-span-2">
            <Select value={form.preferred_callback_time} onValueChange={(v) => setForm({ ...form, preferred_callback_time: v })}>
              <SelectTrigger data-testid="enq-callback"><SelectValue placeholder="Anytime" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="morning">Morning (9-12)</SelectItem>
                <SelectItem value="afternoon">Afternoon (12-4)</SelectItem>
                <SelectItem value="evening">Evening (4-8)</SelectItem>
                <SelectItem value="anytime">Anytime</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("enquiry.notes")} className="sm:col-span-2">
            <textarea data-testid="enq-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input min-h-[100px]" placeholder={t("enquiry.notesPlaceholder")} />
          </Field>

          {/* ─────── Primary contact (used for OTP + receipt) ─────── */}
          <div className="sm:col-span-2 mt-2 -mb-1 text-xs font-semibold uppercase tracking-wide text-ink-soft" data-testid="primary-contact-header">
            Primary contact (we'll send the receipt here)
          </div>
          <Field label={t("enquiry.parentName")} required>
            <NameInput data-testid-wrap value={form.parent_name}
              onChange={(v) => setForm({ ...form, parent_name: v })}
              label="parent name" testid="enq-parent-name"
              placeholder="e.g. Priya Sharma" />
          </Field>
          <Field label="Relationship to child" required>
            <select
              data-testid="enq-relationship"
              required
              value={form.relationship}
              onChange={(e) => setForm({ ...form, relationship: e.target.value, guardian_type: "" })}
              className="input"
            >
              <option value="Mother">Mother</option>
              <option value="Father">Father</option>
              <option value="Guardian">Guardian (other relative / legal guardian)</option>
            </select>
          </Field>
          {form.relationship === "Guardian" && (
            <Field label="Guardian type" required className="sm:col-span-2">
              <select
                data-testid="enq-guardian-type"
                required
                value={form.guardian_type}
                onChange={(e) => setForm({ ...form, guardian_type: e.target.value })}
                className="input"
              >
                <option value="">Select your relationship</option>
                <option value="grandfather">Grandfather</option>
                <option value="grandmother">Grandmother</option>
                <option value="uncle">Uncle</option>
                <option value="aunt">Aunt</option>
                <option value="elder_sibling">Elder sibling</option>
                <option value="legal_guardian">Legal guardian</option>
                <option value="other">Other</option>
              </select>
            </Field>
          )}
          <Field label={t("enquiry.phone")} required>
            <PhoneInput
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              onValidityChange={setPhoneValid}
              required
              placeholder="9844139333"
              testId="enq-phone"
            />
          </Field>
          <Field label={t("enquiry.email")}>
            <input data-testid="enq-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" placeholder="you@gmail.com" />
          </Field>

          {/* ─────── Wave R7 — Family Details (Father / Mother / Guardian) ─────── */}
          <div className="sm:col-span-2 mt-2 mb-2 pt-4 border-t border-border" data-testid="family-details-section">
            <h3 className="text-base font-semibold mb-1">Family details</h3>
            <p className="text-xs text-ink-soft mb-3">
              Tell us a little about both parents — and add a guardian if someone else helps with pickup.
            </p>
            <ContactCard title="Father" tone="sky" testid="father-block"
              value={form.father}
              onChange={(v) => setForm({ ...form, father: v })} />
            <ContactCard title="Mother" tone="rose" testid="mother-block"
              value={form.mother}
              onChange={(v) => setForm({ ...form, mother: v })} />
            <label className="flex items-center justify-between gap-2 rounded-2xl border border-border bg-paper-alt/50 px-3 py-3 mt-2 cursor-pointer" data-testid="guardian-toggle">
              <span className="text-sm font-medium">Add a guardian (grandparent, uncle, aunt, legal guardian)?</span>
              <input type="checkbox"
                checked={form.guardian_enabled}
                onChange={(e) => setForm({ ...form, guardian_enabled: e.target.checked })}
                className="w-5 h-5 accent-terracotta"
                data-testid="guardian-toggle-checkbox" />
            </label>
            {form.guardian_enabled && (
              <ContactCard title="Guardian" tone="leaf" testid="guardian-block" isGuardian
                value={form.guardian}
                onChange={(v) => setForm({ ...form, guardian: v })} />
            )}
          </div>

          <Field label="Send my form number via" className="sm:col-span-2">
            <div className="grid grid-cols-3 gap-2" data-testid="enq-channel-picker">
              {[
                { id: "whatsapp", label: "WhatsApp" },
                { id: "sms", label: "SMS" },
                { id: "email", label: "Email" },
              ].map((c) => {
                const active = form.confirm_channel === c.id;
                return (
                  <button type="button" key={c.id}
                    onClick={() => setForm({ ...form, confirm_channel: c.id })}
                    data-testid={`enq-channel-${c.id}`}
                    className={`h-11 rounded-xl border text-sm font-medium transition ${
                      active ? "border-terracotta bg-terracotta-soft text-terracotta-deep" : "border-border bg-white"
                    }`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-ink-soft mt-1.5">We'll send a short message with your form number + an edit link.</p>
          </Field>
          {err && <div className="text-sm text-red-600 sm:col-span-2" data-testid="enquiry-error">{err}</div>}
          <div className="sm:col-span-2">
            <button disabled={loading} type="submit" data-testid="enquiry-submit-btn"
              className="w-full h-12 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover transition flex items-center justify-center gap-2 disabled:opacity-60 shadow-floating">
              {loading ? t("enquiry.submitting") : (<>{t("enquiry.submit")} <PaperPlaneTilt weight="bold" /></>)}
            </button>
          </div>
        </form>
        {isCoBranded && (
          <div className="mt-10 pt-6 border-t border-border text-xs text-ink-soft text-center" data-testid="enquiry-footer">
            <div>{brand.display_name}{brand.address ? ` · ${brand.address}` : ""}</div>
            <div className="mt-1.5 inline-flex items-center gap-1">
              © {new Date().getFullYear()} {brand.display_name}
              <span className="ml-2 font-bold text-mango-deep">
                Powered by Mango<span className="text-leaf">Cubs</span>
              </span>
            </div>
          </div>
        )}
      </div>
      <style>{`
        .input { width: 100%; height: 48px; padding: 0 16px; border-radius: 12px; border: 1px solid #EBE5DF; background: #fff; outline: none; font-family: 'DM Sans', sans-serif; }
        .input:focus { box-shadow: 0 0 0 2px rgba(229,107,85,0.4); }
        .input::placeholder { color: #BDB3A7; font-weight: 400; opacity: 1; }
        .input::-webkit-input-placeholder { color: #BDB3A7; }
        .input::-moz-placeholder { color: #BDB3A7; opacity: 1; }
        .input:-ms-input-placeholder { color: #BDB3A7; }
        textarea.input { padding: 12px 16px; height: auto; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-sm font-medium text-ink mb-1.5">{label} {required && <span className="text-terracotta">*</span>}</div>
      {children}
    </label>
  );
}

function ContactCard({ title, value, onChange, tone, testid, isGuardian }) {
  const toneCls = {
    sky:  "bg-[#EFF8FF]/40 border-[#B6DEFF]",
    rose: "bg-[#FFF0F5]/40 border-[#FFD0E0]",
    leaf: "bg-[#F1F8E9]/40 border-[#CFE5A5]",
  }[tone];
  // Friendly, concrete examples per role
  const examples = {
    Father:   { name: "e.g. Rohan Sharma", email: "rohan.sharma@gmail.com", occupation: "e.g. Software Engineer" },
    Mother:   { name: "e.g. Priya Sharma", email: "priya.sharma@gmail.com", occupation: "e.g. Doctor" },
    Guardian: { name: "e.g. Lakshmi Devi",  email: "lakshmi.devi@gmail.com", occupation: "e.g. Retired" },
  }[title] || { name: `${title} full name`, email: `${title.toLowerCase()}@example.com`, occupation: "e.g. Architect" };
  return (
    <div className={`rounded-2xl border ${toneCls} p-3 mt-2`} data-testid={testid}>
      <div className="text-sm font-semibold mb-2">{title} details</div>
      <div className="grid sm:grid-cols-2 gap-2.5">
        <NameInput value={value.name}
          onChange={(v) => onChange({ ...value, name: v })}
          placeholder={examples.name} testid={`${testid}-name`}
          label={`${title.toLowerCase()} name`} required={false} />
        <PhoneInput
          value={value.phone}
          onChange={(v) => onChange({ ...value, phone: v })}
          placeholder="9844139333"
          testId={`${testid}-phone`}
        />
        <input className="input" placeholder={examples.email}
          type="email"
          data-testid={`${testid}-email`}
          value={value.email}
          onChange={(e) => onChange({ ...value, email: e.target.value })} />
        <input className="input" placeholder={examples.occupation}
          data-testid={`${testid}-occupation`}
          value={value.occupation}
          onChange={(e) => onChange({ ...value, occupation: e.target.value })} />
        {isGuardian && (
          <select className="input"
            data-testid={`${testid}-type`}
            value={value.guardian_type}
            onChange={(e) => onChange({ ...value, guardian_type: e.target.value })}>
            <option value="">Relationship to child</option>
            <option value="grandfather">Grandfather</option>
            <option value="grandmother">Grandmother</option>
            <option value="uncle">Uncle</option>
            <option value="aunt">Aunt</option>
            <option value="elder_sibling">Elder sibling</option>
            <option value="legal_guardian">Legal guardian</option>
            <option value="other">Other</option>
          </select>
        )}
        {/* WhatsApp number — appears when 'same as mobile' is unchecked. */}
        {!value.whatsapp_same_as_phone && (
          <div className="sm:col-span-2" data-testid={`${testid}-whatsapp-wrap`}>
            <div className="text-xs text-ink-soft mb-1">WhatsApp number (with country code)</div>
            <PhoneInput
              value={value.whatsapp_number}
              onChange={(v) => onChange({ ...value, whatsapp_number: v })}
              placeholder="9844139333"
              testId={`${testid}-whatsapp`}
            />
          </div>
        )}
        {/* Social handles — appear only when the user opts in. */}
        {value.show_socials && (
          <>
            <input className="input" placeholder="Instagram handle (e.g. @priya.sharma)"
              data-testid={`${testid}-instagram`}
              value={value.instagram}
              onChange={(e) => onChange({ ...value, instagram: e.target.value })} />
            <input className="input" placeholder="Facebook handle (e.g. priya.sharma)"
              data-testid={`${testid}-facebook`}
              value={value.facebook}
              onChange={(e) => onChange({ ...value, facebook: e.target.value })} />
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-3 mt-2.5 text-xs text-ink-soft">
        <label className="inline-flex items-center gap-1.5" data-testid={`${testid}-wa-same-toggle`}>
          <input type="checkbox"
            checked={value.whatsapp_same_as_phone}
            onChange={(e) => onChange({ ...value,
              whatsapp_same_as_phone: e.target.checked,
              // Clear the explicit WhatsApp number when re-syncing with mobile.
              whatsapp_number: e.target.checked ? "" : value.whatsapp_number,
            })}
            className="w-4 h-4 accent-terracotta" />
          WhatsApp same as mobile
        </label>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox"
            checked={value.preferred_contact}
            onChange={(e) => onChange({ ...value, preferred_contact: e.target.checked })}
            className="w-4 h-4 accent-terracotta" />
          Preferred contact
        </label>
        <label className="inline-flex items-center gap-1.5" data-testid={`${testid}-socials-toggle`}>
          <input type="checkbox"
            checked={value.show_socials}
            onChange={(e) => onChange({ ...value,
              show_socials: e.target.checked,
              // Clear handles when the toggle is turned off.
              instagram: e.target.checked ? value.instagram : "",
              facebook:  e.target.checked ? value.facebook  : "",
            })}
            className="w-4 h-4 accent-terracotta" />
          Add social handles
        </label>
        {isGuardian && (
          <>
            <label className="inline-flex items-center gap-1.5">
              <input type="checkbox"
                checked={value.pickup_permission}
                onChange={(e) => onChange({ ...value, pickup_permission: e.target.checked })}
                className="w-4 h-4 accent-sage-deep" />
              Pickup permission
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input type="checkbox"
                checked={value.emergency_contact}
                onChange={(e) => onChange({ ...value, emergency_contact: e.target.checked })}
                className="w-4 h-4 accent-terracotta" />
              Emergency contact
            </label>
          </>
        )}
      </div>
    </div>
  );
}
